import { useMemo, useRef } from 'react'
import { useAdmin } from '@vtex/raccoon-next'
import { useAnalytics } from '@/contexts/analytics'
import { ProductSearchParams } from '@/contexts/analytics/customEvents'
import { type transformProductListFiltersToEventProperties } from '@/contexts/analytics/lib/transformProductListFilters'
import { fetcher, type FetchParams } from '@/lib/fetcher'
import { buildQuery } from '@/lib/transform'
import { type Product } from '@/types/product'

import { type RequestParams, useFetch } from './useFetch'
import { useProductOffers } from './useProductOffers'

export const PRODUCT_LISTING_URL = '/api/proxy/catalog/pvt/admin/product'

const extractProductSearchParam = (search?: string) => {
  if (!search) {
    return { 'Search Parameter': ProductSearchParams.none }
  }

  const [searchParamKey, searchTerm = ''] =
    (search.split('=') as [keyof typeof ProductSearchParams, string]) ?? []
  const searchParameter =
    ProductSearchParams[searchParamKey] ??
    `Unrecognized search key: ${searchParamKey}`

  return {
    'Search Parameter': searchParameter,
    'Search Term': searchTerm,
  }
}

type FiltersEventProperties = ReturnType<
  typeof transformProductListFiltersToEventProperties
>

export function useProducts(
  queries: UseProductsQueries,
  bodyQueries: UseProductBodyQueries = {},
  filtersEventProperties: FiltersEventProperties = {} as FiltersEventProperties,
  hasPagination = false
) {
  const { account } = useAdmin()
  const analytics = useAnalytics()
  const isFirstLoad = useRef(true)

  const url = `${PRODUCT_LISTING_URL}?${buildQuery(
    `an=${account}`,
    ...Object.values(queries || {}).filter((query) => !!query)
  )}`

  const body = JSON.stringify(
    Object.fromEntries(
      Object.entries(bodyQueries)
        .filter(([key, value]) => key && value?.length > 0)
        .map(([key, value]) => [key, value.map(String)])
    )
  )

  const { data, error, mutate, isLoading } = useFetch<ProductsDataReturn>(
    url,
    {
      method: 'POST',
      body,
      revalidateIfStale: true,
    },
    {
      fetcher: (params: FetchParams) =>
        fetcher(params, ({ status, loadTimeInSeconds, result = {} }) => {
          const { total = 0 } = result as ProductsDataReturn

          if (Object.keys(filtersEventProperties).length === 0) {
            return
          }

          analytics.track('Load product listing', {
            ...filtersEventProperties,
            ...(isFirstLoad.current
              ? {
                  Action: 'Access product listing',
                }
              : {
                  Action: 'Search',
                  ...extractProductSearchParam(queries.search),
                }),
            'Loading Status': status,
            'Loading Time': loadTimeInSeconds,
            'Search Results': total,
            Pagination: hasPagination,
          })

          isFirstLoad.current = false
        }),
      shouldRetryOnError: () => false,
      onSuccess: () => {
        if (!isFirstLoad.current && window.Intercom) {
          window.Intercom('trackEvent', 'search_for_products_in_admin')
        }
      },
    }
  )

  const { data: products = [], total } = data ?? {}

  const { data: productOffers = [], isLoading: isLoadingOffers } =
    useProductOffers(products?.map((product) => product.id) ?? [])

  const productsWithOffers = useMemo(() => {
    if (isLoadingOffers) {
      return products
    }

    return products?.map((product) => {
      const offer = productOffers?.find(
        (productOffer) => productOffer.ProductId === String(product.id)
      )

      return {
        ...product,
        isVisibleInPortal: !!offer,
        storeUrl: offer?.LinkText ?? (product as ProductWithLink).linkId,
        skus: offer?.Skus ?? [],
        offer,
      }
    })
  }, [isLoadingOffers, productOffers, products])

  return {
    data: { products: productsWithOffers, total },
    error,
    loading: isLoading,
    isLoadingOffers,
    mutate,
  }
}

export function useProductsWithoutOffer(
  queries: UseProductsQueries,
  bodyQueries: UseProductBodyQueries = {},
  requestParams: RequestParams = {}
) {
  const body = JSON.stringify(
    Object.fromEntries(
      Object.entries(bodyQueries)
        .filter(([key, value]) => key && value?.length > 0)
        .map(([key, value]) => [key, value.map(String)])
    )
  )

  const { data, ...rest } = useFetch<ProductsDataReturn>(
    ({ account }) =>
      `${PRODUCT_LISTING_URL}?${buildQuery(
        `an=${account}`,
        ...Object.values(queries || {}).filter((query) => !!query)
      )}`,
    {
      method: 'POST',
      body,
      revalidateIfStale: true,
      ...requestParams,
    }
  )

  return {
    data: data?.data,
    ...rest,
  }
}

type ProductWithLink = Product & {
  linkId: string
}

interface UseProductsQueries {
  search?: string
  hasActiveKit?: string
  isVisible?: string
  isActive?: string
  pagination?: string
  size?: string
  creationDateFrom?: string
  creationDateTo?: string
}

interface UseProductBodyQueries {
  CategoryIds?: string[] | number[]
  BrandIds?: string[] | number[]
  SalesChannelIds?: string[] | number[]
  SellerIds?: string[] | number[]
}

interface ProductsDataReturn {
  data?: Product[]
  total?: number
}
